import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `bonus` domain — the four tabs of `/bonus`. The
 * referral-links and claim-history tabs moved here from `/affiliate`, which
 * now holds only the partner-share pages.
 *
 * Sources read for the copy: `apps/commission/src/repo/commissions.ts` (the
 * hard-coded 1.0% own-commission rate in `KIND_BP`, and the COMM-207 refusal
 * that keeps referral off this screen) and `repo/referral-config.ts` +
 * `engine/referral-income.ts` (the per-brand basis-point accrual the referral
 * tab configures, which is where a referral rate actually comes from).
 */
export const bonusGuide: DomainGuide = {
  domain: 'bonus',
  pages: [
    {
      id: 'bonus.commission',
      route: '/bonus#commission',
      permission: 'commission:read',
      status: 'live',
      fields: [
        { key: 'claimedAt', kind: 'date' },
        { key: 'member', kind: 'text' },
        { key: 'kind', kind: 'status' },
        { key: 'periodKey', kind: 'text' },
        {
          key: 'amount',
          kind: 'money-unsigned',
          formula:
            'amount = houseWin × 100 / 10000   (own commission only; a referral row is accrued by the referral programme at its configured basis points, not by this formula)',
        },
      ],
      cautionCount: 6,
      warningCount: 0,
    },
    {
      id: 'bonus.referral',
      route: '/bonus#referral',
      permission: 'commission:read',
      status: 'live',
      fields: [
        { key: 'scope', kind: 'text' },
        { key: 'enabled', kind: 'status' },
        {
          key: 'lossShareBp',
          kind: 'basis-points',
          formula: 'lossShare = Σ refereeLoss × lossShareBp / 10000',
        },
        {
          key: 'turnoverShareBp',
          kind: 'basis-points',
          formula: 'turnoverShare = Σ refereeTurnover × turnoverShareBp / 10000',
        },
        {
          key: 'subLineShareBp',
          kind: 'basis-points',
          formula: 'subLineShare = Σ subRefereeLoss × subLineShareBp / 10000',
        },
        {
          key: 'ftdBonusPerHead',
          kind: 'money-unsigned',
          formula: 'ftdBonus = ftdHeads × ftdBonusPerHead',
        },
        {
          key: 'minClaimAmount',
          kind: 'money-unsigned',
          formula: 'claimable = accrued − claimed ≥ max(minClaimAmount, 1)',
        },
        {
          key: 'ftdMinDeposit',
          kind: 'money-unsigned',
          formula: 'ftdHead counted only when firstApprovedDeposit ≥ ftdMinDeposit',
        },
        { key: 'claimMode', kind: 'text' },
        { key: 'claimsPerDay', kind: 'count' },
        { key: 'claimDow', kind: 'text' },
        { key: 'claimDom', kind: 'text' },
        {
          key: 'expiryDays',
          kind: 'count',
          formula:
            'forfeit min(unclaimedNow, claimableAtOpening) when bangkokDaysSince(anchor) >= expiryDays, and only if claimableAtOpening >= minClaimAmount   (deadline anchor = max(firstOpeningAtOrAfter(configSavedAt), last claim; weekly/monthly: last window opening) — the AMOUNT is always measured at the un-floored opening)',
        },
      ],
      cautionCount: 6,
      warningCount: 2,
    },
    {
      id: 'bonus.referrals',
      route: '/bonus#referralLinks',
      permission: 'commission:read',
      status: 'live',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'referrerKind', kind: 'status' },
        { key: 'referrerId', kind: 'text' },
        { key: 'refereeId', kind: 'text' },
      ],
      cautionCount: 4,
      warningCount: 0,
    },
    {
      id: 'bonus.commissionClaims',
      route: '/bonus#claimHistory',
      permission: 'commission:read',
      status: 'live',
      fields: [
        { key: 'claimedAt', kind: 'date' },
        { key: 'memberId', kind: 'text' },
        { key: 'kind', kind: 'status' },
        { key: 'periodKey', kind: 'text' },
        { key: 'amount', kind: 'money-unsigned' },
        { key: 'txId', kind: 'text' },
      ],
      cautionCount: 4,
      warningCount: 0,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
